import { useState } from 'react';
import {
  type AgentProfile,
  type UpdateAgentProfileRequest,
  useInvalidateUseGetAgentProfile,
  useInvalidateUseGetAgentsInAgency,
  useUpdateAgentProfile,
} from '@urbanx/agx.agencies.client';
import {
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTextInput,
  SAVE_BUTTON_STATES,
  useToast,
} from '@urbanx/agx.ui-components2';
import { isEqual } from 'lodash-es';

import ProfilePhotoUpload from './ProfilePhotoUpload';
import '../ProfileSettingsPage.scss';

interface ProfileSettingsFormProps {
  agentProfile: AgentProfile;
}

const ProfileSettingsForm = ({ agentProfile }: ProfileSettingsFormProps) => {
  const { pushToast } = useToast();
  const invalidateAgentProfile = useInvalidateUseGetAgentProfile();
  const invalidateAgentsInAgency = useInvalidateUseGetAgentsInAgency();
  const createForm = (): UpdateAgentProfileRequest => ({
    firstName: agentProfile.firstName,
    lastName: agentProfile.lastName,
    title: agentProfile.title,
    phone: agentProfile.phone,
    photo: agentProfile.photo,
  });
  const [profile, setProfile] = useState<UpdateAgentProfileRequest>(createForm);
  const [initialProfile, setInitialProfile] = useState<UpdateAgentProfileRequest>(createForm);
  const [changesSaved, setChangesSaved] = useState(false);

  const { updateAgentProfile, updateAgentProfilePending } = useUpdateAgentProfile({
    onSuccess: async (_, inputData) => {
      setProfile(inputData);
      setInitialProfile(inputData);
      setChangesSaved(true);
      await Promise.all([invalidateAgentProfile(), invalidateAgentsInAgency()]);
    },
    onError: () => {
      pushToast({
        type: 'error',
        message: 'Unable to save profile changes',
      });
    },
  });

  const isDirty = !isEqual(profile, initialProfile);
  const hasRequiredNames =
    profile.firstName.trim().length > 0 && profile.lastName.trim().length > 0;
  const saveButtonState = updateAgentProfilePending
    ? SAVE_BUTTON_STATES.SAVING
    : changesSaved && !isDirty
      ? SAVE_BUTTON_STATES.SAVED
      : SAVE_BUTTON_STATES.UNSAVED;

  const saveProfile = async () => {
    if (!isDirty || !hasRequiredNames || updateAgentProfilePending) return;

    setChangesSaved(false);
    try {
      await updateAgentProfile({
        ...profile,
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
      });
    } catch {
      return;
    }
  };

  return (
    <AgxSpacer className="profile-settings" gap="spacing-3xl">
      <ProfilePhotoUpload
        disabled={updateAgentProfilePending}
        firstName={profile.firstName}
        lastName={profile.lastName}
        photo={profile.photo}
        onPhotoChanged={(photo) => {
          setChangesSaved(false);
          setProfile({ ...profile, photo });
        }}
      />

      <AgxSpacer gap="spacing-2xl">
        <AgxTextInput
          id="profile-email"
          type="email"
          label="Email"
          hideOptionalText
          stretch
          defaultValue={agentProfile.email}
          disabled
        />
        <AgxSpacer direction="horizontal" gap="spacing-2xl" wrap>
          <AgxTextInput
            id="profile-first-name"
            type="text"
            label="First name"
            required
            hideOptionalText
            stretch
            defaultValue={profile.firstName}
            onInputValueChange={({ value }) => {
              setChangesSaved(false);
              setProfile({ ...profile, firstName: value });
            }}
          />
          <AgxTextInput
            id="profile-last-name"
            type="text"
            label="Last name"
            required
            hideOptionalText
            stretch
            defaultValue={profile.lastName}
            onInputValueChange={({ value }) => {
              setChangesSaved(false);
              setProfile({ ...profile, lastName: value });
            }}
          />
        </AgxSpacer>

        <AgxTextInput
          id="profile-job-title"
          type="text"
          label="Job title"
          hideOptionalText
          stretch
          defaultValue={profile.title}
          onInputValueChange={({ value }) => {
            setChangesSaved(false);
            setProfile({ ...profile, title: value });
          }}
        />
        <AgxTextInput
          id="profile-phone"
          type="phone"
          label="Phone"
          hideOptionalText
          stretch
          defaultValue={profile.phone}
          onInputValueChange={({ value }) => {
            setChangesSaved(false);
            setProfile({ ...profile, phone: value });
          }}
        />
      </AgxSpacer>

      <AgxSpacer direction="horizontal" contentJustification="start">
        <AgxSaveChangesButton
          id="save-agent-profile"
          text="Save changes"
          variant="primary"
          size="medium"
          state={saveButtonState}
          onClick={saveProfile}
          disabled={!isDirty || !hasRequiredNames || updateAgentProfilePending}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default ProfileSettingsForm;
